import React, { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Package } from "lucide-react";
import { useEstatisticasEntrega } from "../context/EstatisticasEntregaContext";
import { entregaService, Entrega } from "../../../../services/entregaService";

interface EntregadorPedidos {
  entregadorId: number;
  nome: string;
  pedidos: number[];
}

function aggregatePedidosByEntregador(
  entregas: Entrega[]
): EntregadorPedidos[] {
  const byEntregador = new Map<number, { nome: string; ids: number[] }>();

  for (const e of entregas) {
    const id = e.entregador.id;
    const nome = e.entregador.nome ?? "—";
    const ids = e.pedidosDisplayIds ?? [];

    if (!byEntregador.has(id)) {
      byEntregador.set(id, { nome, ids: [] });
    }
    const row = byEntregador.get(id)!;
    row.ids.push(...ids);
  }

  return Array.from(byEntregador.entries())
    .map(([entregadorId, { nome, ids }]) => ({
      entregadorId,
      nome,
      pedidos: ids.filter(Number.isInteger).sort((a, b) => a - b),
    }))
    .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));
}

const TabelaPedidosPorEntregador: React.FC = () => {
  const { appliedFilters } = useEstatisticasEntrega();

  const { data, isLoading } = useQuery({
    queryKey: [
      "entregas-pedidos-por-entregador",
      appliedFilters.dataInicio,
      appliedFilters.dataFim,
      appliedFilters.lojaId,
      appliedFilters.usuarioId,
    ],
    queryFn: () =>
      entregaService.getAll({
        dataInicio: appliedFilters.dataInicio,
        dataFim: appliedFilters.dataFim,
        lojaId: appliedFilters.lojaId ?? undefined,
        entregadorId: appliedFilters.usuarioId ?? undefined,
        limit: 1000,
        page: 1,
      }),
    enabled: !!(appliedFilters.dataInicio && appliedFilters.dataFim),
  });

  const rows = useMemo(() => {
    if (!data?.data?.length) return [];
    return aggregatePedidosByEntregador(data.data);
  }, [data?.data]);

  if (isLoading) {
    return (
      <div className="rounded-lg bg-white p-6 shadow">
        <h3 className="mb-4 flex items-center gap-2 text-lg font-medium text-neutral-900">
          <Package size={20} />
          Pedidos por entregador
        </h3>
        <div className="py-8 text-center text-neutral-500">Carregando...</div>
      </div>
    );
  }

  return (
    <div className="rounded-lg bg-white p-6 shadow">
      <h3 className="mb-4 flex items-center gap-2 text-lg font-medium text-neutral-900">
        <Package size={20} />
        Pedidos por entregador
      </h3>

      {rows.length === 0 ? (
        <div className="py-8 text-center text-neutral-500">
          Nenhum pedido encontrado para o período e filtros selecionados.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[320px] border-collapse text-left">
            <thead>
              <tr className="border-b border-neutral-200 bg-neutral-50">
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-neutral-600">
                  Entregador
                </th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-neutral-600">
                  Números dos pedidos
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.entregadorId}
                  className="border-b border-neutral-100 transition-colors hover:bg-neutral-50/50"
                >
                  <td className="px-4 py-3 font-medium text-neutral-900">
                    {row.nome}
                  </td>
                  <td className="px-4 py-3 text-neutral-700">
                    {row.pedidos.length > 0 ? (
                      <span className="inline-flex flex-wrap gap-1">
                        {row.pedidos.map((num) => (
                          <span
                            key={num}
                            className="rounded bg-neutral-100 px-1.5 py-0.5 text-sm tabular-nums"
                          >
                            {num}
                          </span>
                        ))}
                      </span>
                    ) : (
                      <span className="text-neutral-400">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default TabelaPedidosPorEntregador;
